import Mustache from 'mustache';
import localizations from './localizations';
import throttle from 'lodash.throttle';
import debounce from 'lodash.debounce';

import { VehicleSearchQuicks } from './vehicle-search-quicks';
import { VehicleSearchFlex } from './vehicle-search-flex';
import { VehicleConditionalFilters } from './vehicle-search-conditionals';
import dataLayer from './data-layer';

/**
 * Search vehicles from an ajax endpoint and render them via mustache
 *
 * @class VehicleSearch
 */
class VehicleSearch {
  constructor() {
    this.vehicleSearchRoot = document.querySelector('#vehicle-search');
    this.vehicleSearchParent = document.querySelector('.subpage');
    this.vehicleSearchForm = document.querySelector('#vehicle-search-form');
    this.vehicleSearchSidebar = document.querySelector('.vehicle-search__sidebar');
    this.cardListWrapper = document.querySelector('.card-list--wrapper');
    //this.loadingSpinner = document.querySelector('#vehicle-search-spinner');
    this.orderControl = document.querySelector('.js-vehicle-search-sort');
    this.nextPageButton = document.querySelector('.js-vehicle-search-next-page');
    this.prevPageButton = document.querySelector('.js-vehicle-search-prev-page');

    if (window.r3multiselects) {
      this.multiSelects = Object.values(window.r3multiselects).filter(
        m => m.element.closest('#vehicle-search-form') || m.element.id === 'order',
      );
    }

    this.rootUrl = this.vehicleSearchRoot.dataset.rootUrl;

    this.templateVehicleList = document.querySelector('#mustache-vehicle-list').innerHTML;
    this.templateVehicleCard = document.querySelector('#mustache-vehicle-card').innerHTML;
    this.templateAdvertisementCard = document.querySelector('#mustache-advertisement-card').innerHTML;
    this.templateVehicleListHeading = document.querySelector('#mustache-vehicle-list-heading').innerHTML;
    this.templateVehicleListPaging = document.querySelector('#mustache-vehicle-list-paging').innerHTML;
    this.templateVehicleListPictureSource = document.querySelector('#mustache-vehicle-list-picturesource').innerHTML;

    this.submitHandler = this.submitHandler.bind(this);
    this.resetHandler = this.resetHandler.bind(this);
    this.changeHandler = throttle(this.changeHandler.bind(this), 1000, { trailing: true, leading: true });
    this.toggleFilters = this.toggleFilters.bind(this);
    this.orderClickHandler = this.orderClickHandler.bind(this);
    this.popStateHandler = this.popStateHandler.bind(this);
    this.showMoreFilters = this.showMoreFilters.bind(this);
    this.menuCloser = this.menuCloser.bind(this);
    this.clearFilters = this.clearFilters.bind(this);
    this.makeSelectCloser = this.makeSelectCloser.bind(this);
    this.selectChangeHandler = throttle(this.selectChangeHandler.bind(this), 1000, { trailing: true, leading: true });
    this.pushToDataLayer = throttle(this.pushToDataLayer.bind(this), 1000, { trailing: true, leading: true });
    this.pushSearchToDataLayer = throttle(this.pushSearchToDataLayer.bind(this), 1000, { trailing: true, leading: false });

    this.pushDelayedAnalytics = this.pushDelayedAnalytics.bind(this);
    this.pushDelayedAnalyticsDebounced = debounce(this.pushDelayedAnalytics, 10_000, {
      leading: false,
      trailing: true,
    });

    document.onvisibilitychange = () => {
      if (document.visibilityState === 'hidden') {
        this.pushDelayedAnalytics();
      }
    };

    this.vehicleSearchQuicks = new VehicleSearchQuicks(this);
    this.VehicleSearchFlex = new VehicleSearchFlex(this);
    this.vehicleConditionalFilters = new VehicleConditionalFilters(this);
  }

  multiSelects = [];

  /**
   * Initialize vehicle search
   *
   * @memberof VehicleSearch
   */
  init() {
    this.initFilters();
    const { queryString, rawObject } = this.#serializeForm(this.vehicleSearchForm);
    this.queryString = queryString;

    this.vehicleConditionalFilters.evaluateConditions(rawObject);
    setTimeout(() => {
      this.filtersCount = this.vehicleSearchQuicks.updateChips(rawObject);
      this.updateFiltersCount();
    }, 500);

    const { totalResults } = this.cardListWrapper.dataset;

    this.#updateVehicleCounts({ paging: { totalResults: totalResults } });
  }

  /**
   * Serialize form element into query string
   *
   * @param {*} form form element
   * @return {string} query string
   * @memberof VehicleSearch
   */
  #serializeForm(form) {
    let formData = new FormData(form);

    const numberInputs = document.querySelectorAll('[type="number"][data-blank-value]');
    [...numberInputs].map(input => {
      if (input.value === input.dataset.blankValue) {
        formData.delete(input.name);
      }
    });

    const tabs = document.querySelectorAll('[data-tabs-control]');
    [...tabs].map(input => {
      formData.delete(input.name);
    });

    let queryParams = [];
    const rawObject = [...formData.entries()];

    for (let pair of rawObject) {
      if (pair[0] && pair[1]) queryParams.push(pair[0] + '=' + pair[1]);
    }

    const { prettyUrl, prettyFormData } = this.makePrettyUrl(formData);

    let prettyQueryParams = [];
    for (let pair of prettyFormData.entries()) {
      if (pair[0] && pair[1]) prettyQueryParams.push(pair[0] + '=' + pair[1]);
    }

    const paramsString = prettyQueryParams.length ? '?' + prettyQueryParams.join('&') : '';
    let prettyQueryString = queryParams.length ? encodeURI(prettyUrl + paramsString) : '';
    const queryString = queryParams.length ? encodeURI('?' + queryParams.join('&')) : '';

    return { queryString, prettyQueryString, rawObject };
  }

  /**
   * Format URL neatly
   *
   * @param {FormData} formData form data object
   * @returns {object}
   */
  makePrettyUrl(formData) {
    let prettyFormData = formData;
    let search_type = formData.getAll('search_type[]').filter(Boolean);
    if (search_type.length !== 1) search_type = undefined;
    const make = formData.getAll('make[]').filter(Boolean);
    const model = formData.getAll('model[]').filter(Boolean);
    let prettyUrl = '';

    if (model.length && model.length === 1) {
      // If all parts
      prettyFormData.delete('search_type[]');
      prettyFormData.delete('make[]');
      prettyFormData.delete('model[]');
      prettyUrl = `${search_type ?? 'kaikki'}/${make.length === 1 ? make : 'kaikki'}/${model}`;
    } else if (make.length === 1) {
      // If make
      prettyFormData.delete('search_type[]');
      prettyFormData.delete('make[]');
      prettyUrl = `${search_type ?? 'kaikki'}/${make}/`;
    } else if (search_type) {
      // If only category
      prettyFormData.delete('search_type[]');
      prettyUrl = `${search_type ?? 'kaikki'}/`;
    }

    return { prettyUrl, prettyFormData };
  }

  /**
   * Empty vehicle list so new vehicles can be shown
   *
   * @memberof VehicleSearch
   */
  #clearVehicleList() {
    //this.loadingSpinner.classList.remove('d-none');
    this.cardListWrapper.innerHTML = '';

    document.querySelector('#vehicle-search-seo')?.remove();
  }

  queryString = '';

  /**
   * Get vehicles from ajax endpoint with selected form filters
   *
   * @param {object} form vehicle search form element
   * @returns {object} request response or rejected promise
   * @memberof VehicleSearch
   */
  #getVehicles(form) {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      const { queryString, prettyQueryString, rawObject } = this.#serializeForm(form);

      this.filtersCount = this.vehicleSearchQuicks.updateChips(rawObject);
      this.updateFiltersCount();

      if (this.queryString.localeCompare(queryString) === 0) return; // Same query string, no changes...
      this.queryString = queryString;

      this.#clearVehicleList();
      this.vehicleConditionalFilters.evaluateConditions(rawObject);

      const cleanUrl = [window.location.origin, '/', this.rootUrl, prettyQueryString].join('');
      history.pushState({ queryString: prettyQueryString }, '', cleanUrl);

      // Add updated URL to search to ensure pagination works as expected
      const cleanUrlString = ['/', this.rootUrl, prettyQueryString].join('');
      const cleanUrlStringParam = queryString ? `&source_url=${encodeURIComponent(cleanUrlString)}` : `?source_url=${encodeURIComponent(cleanUrlString)}`;

      request.open('GET', '/stage-ajax/getVehicles' + queryString + cleanUrlStringParam + '&extendedFuelTypes=1', true);
      request.send();
      request.onload = () => {
        if (request.readyState === 4 && request.status === 200) {
          resolve(request.response);
        } else reject(request);
      };

      request.onerror = () => reject(request);
    });
  }

  /**
   * Updates counter showing how many filters are selected
   */
  updateFiltersCount() {
    const selectedFiltersContainer = document.querySelector('#vehicle-search-filters-selected-container');
    const selectedFiltersCount = document.querySelector('#vehicle-search-filters-selected');
    selectedFiltersContainer.classList.toggle('hide', !this.filtersCount);
    selectedFiltersCount.innerText = this.filtersCount;
  }

  /**
   * Render ajax response as html with mustache
   *
   * @param {object} response raw json from ajax
   * @memberof VehicleSearch
   */
  #renderVehicles(response) {
    response = JSON.parse(response);
    //this.loadingSpinner.classList.add('d-none');

    const cardListTitle = document.querySelector('.vehicle-search__title');
    cardListTitle.outerHTML = Mustache.render(this.templateVehicleListHeading, response);

    const cardListPaging = document.querySelector('.vehicle-search__paging');
    cardListPaging.outerHTML = Mustache.render(this.templateVehicleListPaging, response);
    this.#updateVehicleCounts(response);

    if (localizations.page_title) document.title = localizations.page_title;

    this.cardListWrapper.innerHTML = Mustache.render(this.templateVehicleList, response, {
      vehicleCard: this.templateVehicleCard,
      advertisementCard: this.templateAdvertisementCard,
      pictureSource: this.templateVehicleListPictureSource,
    });

    if (!response.paging.totalResults) {
      this.cardListWrapper.querySelector('.button--show-filters')?.addEventListener('click', this.toggleFilters);
    }
  }

  #updateVehicleCounts(response) {
    const showResultsFloatButton = document.querySelector('#sidebar-toggle-open [data-label]');
    const { label } = showResultsFloatButton.dataset;
    showResultsFloatButton.innerText = label.replace('%amount%', response.paging.totalResults);

    const showResultsBottomButton = document.querySelector('#vehicle-search-fa-show-vehicles [data-label]');
    showResultsBottomButton.innerText = label.replace('%amount%', response.paging.totalResults);

    const bottomVehicleResultsCount = document.querySelector('.vehicle-search__mobile-nav [data-id="vehicle-results-count"]');
    if (bottomVehicleResultsCount) bottomVehicleResultsCount.innerText = response.paging.totalResults;
  }

  /**
   * Show error message to user
   *
   * @param {*} error error message
   * @memberof VehicleSearch
   */
  #renderError(error) {
    //this.loadingSpinner.classList.add('d-none');
    this.cardListWrapper.innerHTML = `<h3 class="error-text">${localizations.general_error}</h3>`;
    console.log(error);
  }

  /**
   * Create event related actions for vehicle search elements
   *
   * @memberof VehicleSearch
   */
  initFilters() {
    Object.keys(window.r3multiselects).forEach(select => {
      if (['make', 'quick-make'].includes(select)) window.r3multiselects[select].element.addEventListener('change', this.makeSelectCloser);
      else window.r3multiselects[select].element.addEventListener('change', this.selectChangeHandler);
    });
    this.vehicleSearchForm.addEventListener('submit', this.submitHandler);
    this.vehicleSearchForm.addEventListener('reset', this.resetHandler);
    this.vehicleSearchForm.addEventListener('change', this.changeHandler);

    this.orderControl.addEventListener('change', this.orderClickHandler);
    document.addEventListener('click', this.menuCloser);

    window.onpopstate = this.popStateHandler;

    const searchFilterToggle = document.querySelectorAll('.js-toggle-vehicle-search-filters, .vehicle-search__heading');
    searchFilterToggle.forEach(button => button.addEventListener('click', this.toggleFilters));

    const resetFilters = document.querySelectorAll('.js-reset-vehicle-search-filters');
    resetFilters.forEach(button => button.addEventListener('click', this.clearFilters));

    const showMoreFiltersButton = document.querySelector('.js-vehicle-search-show-more-filters');
    const additionalFilters = document.querySelector('.vehicle-search__additional-filters');
    if (showMoreFiltersButton && additionalFilters) {
      showMoreFiltersButton.addEventListener('click', this.showMoreFilters);
    }
  }

  /**
   * handle history related state changes.
   * @param {Event} event - The event object.
   * @return {boolean}
   */
  popStateHandler(event) {
    if (!event.state) return false;

    //hide filters on mobile on back press.
    if (typeof event.state.filterOpen === 'undefined' && this.filtersOpen()) {
      this.closeFilters();
    }
    if (event.state.filterOpen === true && this.filtersOpen()) {
      this.closeFilters();
    } else if (event.state.filterOpen === false) {
      this.openFilters();
    }

    //handle vehicle search history push
    if (event.state.queryString) {
      window.location.href = window.location.pathname + event.state.queryString;
    }
  }

  /**
   * add js to ordering control select input as it's placed outside the html form element
   * @param {Event} event - The event object.
   */
  orderClickHandler(event) {
    const orderInput = this.vehicleSearchForm.querySelector('input[name="order"]');
    orderInput.value = event.target.value;
    this.vehicleSearchForm.dispatchEvent(new Event('change'));
  }

  selectChangeHandler(event) {
    this.pushToDataLayer(event);
  }

  /**
   * Reset the form
   * @param {Event} _event - The event object.
   */
  clearFilters(_event) {
    this.vehicleSearchForm.reset();
  }

  /**
   * hide and show filters for mobile users
   * @param {Event} event - The event object.
   */
  toggleFilters(event) {
    if (event.target.closest('.js-toggle-vehicle-search-filters')) {
      event.stopPropagation();
      if (this.filtersOpen()) {
        this.closeFilters();
        history.pushState({ filterOpen: true }, null, '');
      } else {
        this.openFilters();
        history.pushState({ filterOpen: false }, null, '');
      }
    }
  }

  /**
   * shows more filters if there are hidden secondary filters
   * @param {Event} event - The event object.
   */
  showMoreFilters(event) {
    const button = event.target.closest('.js-vehicle-search-show-more-filters');
    const text = button.querySelector('span');
    const additionalFiltres = document.querySelector('.vehicle-search__additional-filters');

    additionalFiltres.classList.toggle('hide');
    const state = additionalFiltres.classList.contains('hide');
    text.innerText = state ? button.dataset.labelClosed : button.dataset.labelOpen;
  }

  /**
   * dispatch change event to trigger js for multiselect fields.
   * @param {Event} _event - The event object.
   */
  resetHandler(_event) {
    document.location = [window.location.origin, '/', this.rootUrl].join('');

    // setTimeout(() => {
    //   this.multiSelects.forEach(s => s.unSelectAll());

    //   this.vehicleSearchForm.querySelectorAll('[type=checkbox],[type=radio]:not([data-tabs-control])').forEach(checky => {
    //     checky.removeAttribute('checked');
    //     checky.dispatchEvent(new Event('change', { bubbles: true }));
    //   });

    //   this.vehicleSearchForm.querySelectorAll('[type=number]').forEach(number => {
    //     number.value = parseInt(number.dataset?.blankValue);
    //     number.dispatchEvent(new Event('change', { bubbles: true }));
    //   });

    //   const changeEvent = new Event('change');
    //   this.vehicleSearchForm.dispatchEvent(changeEvent);
    // }, 5);
  }

  /**
   * Handles submit event
   * @param {Event} event - The event object.
   */
  submitHandler(event) {
    event.preventDefault();

    this.#getVehicles(event.target)
      .then(vehicleData => {
        this.#renderVehicles(vehicleData);
      })
      .catch(errorMessage => {
        this.#renderError(errorMessage);
      });

    this.pushSearchToDataLayer();
  }

  /**
   * trigger search after filter change (with buffer).
   * @param {Event} event - The event object.
   */
  changeHandler(event) {
    this.vehicleSearchForm.dispatchEvent(new Event('submit', { cancelable: true }));
    this.pushToDataLayer(event);
  }

  /**
   * Open filter element for mobile users
   *
   * @memberof VehicleSearch
   */
  openFilters() {
    this.vehicleSearchSidebar.classList.add('open');
    this.vehicleSearchForm.toggleAttribute('inert', false);
    this.focusFirstFocusableElement(this.vehicleSearchSidebar);
  }

  /**
   * Close filter element for mobile users
   *
   * @memberof VehicleSearch
   */
  closeFilters() {
    if (this.vehicleSearchSidebar.classList.contains('open')) {
      this.vehicleSearchSidebar.classList.remove('open');
      this.vehicleSearchForm.toggleAttribute('inert', true);
      this.focusFirstFocusableElement(this.cardListWrapper);
    }
  }

  focusFirstFocusableElement(element) {
    const focusableElements = element.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');

    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  /**
   * filtersOpen
   *
   * @return {boolean} is filter element open or no
   * @memberof VehicleSearch
   */
  filtersOpen() {
    return this.vehicleSearchSidebar.classList.contains('open');
  }

  /**
   * Listens for clicks that should close the filter menu
   *
   * @param {Event} {target} - The event object.
   */
  menuCloser({ target }) {
    if (this.filtersOpen && !target.closest('.vehicle-search__sidebar') && !target.closest('.js-toggle-vehicle-search-filters'))
      this.closeFilters();
  }

  /**
   * Even though make select is a multiselect, they want it to close upon selecting first make.
   * This handler catches change events on the two make multiselects, and attempts to close them on activation.
   *
   * @param {Event} event change event on make multiselect
   */
  makeSelectCloser(event) {
    try {
      const container = event.target.closest('.r3multiselect__container');
      container.multiSelectObject.closeOptionsList();
      setTimeout(() => container.querySelector('[type="search"]')?.blur(), 50);
      this.selectChangeHandler(event);
    } catch (e) {
      console.log(e);
    }
  }

  lastEventPush = null;

  /**
   * Push to analytics changes on the form.
   *
   * @param {Event} event HTML event of the change
   */
  pushToDataLayer(event) {
    const target = event.target === this.vehicleSearchForm ? event?.detail?.target : event.target;

    if (target) {
      let formData = new FormData(this.vehicleSearchForm);
      const values = formData.getAll(target.name).filter(Boolean);
      const eventLabel = `${target.name}: ${values.join()}`;
      if (eventLabel !== this.lastEventPush) {
        this.lastEventPush = eventLabel;
        console.log(`%c${eventLabel}`, 'color: lightgreen; background-color: #333;'); // to-do: remove this. Just for testing.
        dataLayer.push({
          event: 'GAEvent',
          eventAction: 'Search',
          eventLabel: eventLabel,
          nonInteraction: false,
        });
      }
    }
  }

  /**
   * Can be triggered to send a debounced analytics event. Called by either page exiting event, or by debounce trailing event being fired.
   */
  pushDelayedAnalytics() {
    if (this.pendingVehicleSearchEvent) {
      console.log(`%c${JSON.stringify(this.pendingVehicleSearchEvent, 0, 2)}`, 'color: gold; background-color: #333;'); // to-do: remove this. Just for testing.
      dataLayer.push(this.pendingVehicleSearchEvent);
      this.pendingVehicleSearchEvent = null;
    }
  }

  pendingVehicleSearchEvent = null;

  /**
   * Push to analytics the form submit.
   */
  pushSearchToDataLayer() {
    const { rawObject } = this.#serializeForm(this.vehicleSearchForm);
    const excludedKeys = ['page'];

    const searchObject = {};
    rawObject.forEach(([key, val]) => {
      if (!excludedKeys.includes(key)) {
        key = key.replace('[]', '');
        if (!searchObject[key]) searchObject[key] = [];
        if (val) searchObject[key].push(val);
      }
    });

    const dataLayerObject = {
      event: 'vehicle_search',
      vehicle_search_used_vehicle: searchObject.search_type?.includes('vaihtoautot') || searchObject.search_type?.length === 0,
      vehicle_search_new_vehicle: searchObject.search_type?.includes('uudet-autot') || searchObject.search_type?.length === 0,
      vehicle_search_demo_vehicle: searchObject.search_type?.includes('esittelyautot') || searchObject.search_type?.length === 0,
    };

    Object.entries(searchObject).forEach(([key, val]) => {
      if (val.length) dataLayerObject['vehicle_search_' + key] = val.join(',');
    });

    this.pendingVehicleSearchEvent = dataLayerObject;
    this.pushDelayedAnalyticsDebounced();
  }
}

export default new VehicleSearch();
